import React, { useCallback, useEffect, useState } from "react";
import { FaPlus, FaTrash, FaUserCog } from "react-icons/fa";
import { apihubDelete, apihubGet } from "../../api/apihub";
import { postCreateattribute } from "../../api/customer.api";
import { useProjectPhone } from "../../hooks/useProjectPhone";

const emptyRow = () => ({ name: "", action: "" });

const Attribute = () => {
  const { projectId, phoneNoId } = useProjectPhone();
  const [rows, setRows] = useState([emptyRow()]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [duplicateError, setDuplicateError] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const savedAttributes = detail?.data?.attributes || [];
  const savedActions = detail?.data?.actions || [];

  const load = useCallback(async () => {
    if (!phoneNoId) return;
    setLoading(true);
    setError("");
    try {
      const res = await apihubGet(
        `/getInteractedCustomerDetail/getattributesdetail/${phoneNoId}`,
      );
      setDetail(res);
    } catch (err) {
      setDetail(null);
      setError(err.message || "Could not load attributes");
    } finally {
      setLoading(false);
    }
  }, [phoneNoId]);

  useEffect(() => {
    load();
  }, [load]);

  const existingNames = new Set(
    savedAttributes.map((a) => String(a || "").trim().toLowerCase()),
  );

  const updateRow = (index, e) => {
    const { name, value } = e.target;
    const next = [...rows];
    next[index] = { ...next[index], [name]: value };
    setRows(next);

    if (name === "name") {
      const trimmed = value.trim().toLowerCase();
      const dupInForm = next.some(
        (r, i) =>
          i !== index && (r.name || "").trim().toLowerCase() === trimmed,
      );
      const dupSaved = trimmed && existingNames.has(trimmed);
      setDuplicateError(
        dupInForm || dupSaved
          ? "This name already exists in the attributes list."
          : "",
      );
    }
  };

  const addRow = () => {
    setRows((prev) => [...prev, emptyRow()]);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!phoneNoId) return;
    if (duplicateError) {
      setError(duplicateError);
      return;
    }

    const names = rows.map((r) => (r.name || "").trim());
    const actions = rows.map((r) => r.action || "");

    if (names.some((n) => !n)) {
      setError("Attribute name is required.");
      return;
    }

    setSaving(true);
    setError("");
    setMessage("");
    try {
      await postCreateattribute({
        phoneNoId,
        projectId,
        attributes: names,
        action: actions,
      });
      setMessage(
        names.length > 1
          ? "User attributes created successfully."
          : "User attribute created successfully.",
      );
      setRows([emptyRow()]);
      setModalOpen(false);
      load();
    } catch (err) {
      setError(err.message || "Failed to save attributes");
    } finally {
      setSaving(false);
    }
  };

  const removeSaved = async (index) => {
    const attrName = savedAttributes[index];
    if (!attrName || !phoneNoId) return;
    if (!window.confirm(`Delete attribute "${attrName}"?`)) return;

    setSaving(true);
    setError("");
    try {
      await apihubDelete(
        `/getInteractedCustomerDetail/deleteattribute/${phoneNoId}/${encodeURIComponent(attrName)}`,
      );
      setMessage("Attribute deleted successfully.");
      load();
    } catch (err) {
      setError(err.message || "Failed to delete attribute");
    } finally {
      setSaving(false);
    }
  };

  if (!projectId) {
    return (
      <div className="biz-panel-page">
        <p className="biz-table-message">No project selected.</p>
      </div>
    );
  }

  return (
    <div className="biz-panel-page">
      <div className="biz-panel-header biz-panel-header-stack">
        <div>
          <h1>
            <FaUserCog />
            User Attributes
          </h1>
          <p className="biz-panel-subtitle">
            Attributes store Dialogflow parameter values. You can set custom
            values from the contacts page.
          </p>
        </div>
        <button
          type="button"
          className="biz-btn-primary"
          onClick={() => {
            setModalOpen(true);
            setError("");
            setMessage("");
            setDuplicateError("");
            setRows([emptyRow()]);
          }}
        >
          <FaPlus />
          Add User Attribute
        </button>
      </div>

      {error && <p className="biz-form-error">{error}</p>}
      {message && <p className="biz-form-success">{message}</p>}

      {loading ? (
        <p className="biz-table-message">Loading attributes…</p>
      ) : savedAttributes.length === 0 ? (
        <p className="biz-table-empty biz-attr-empty">
          There is no saved attribute
        </p>
      ) : (
        <div className="biz-attr-section">
          <h3 className="biz-attr-saved-title">Saved Attributes</h3>
          <div className="biz-table-wrap">
            <table className="biz-table">
              <thead>
                <tr>
                  <th className="biz-col-index">#</th>
                  <th>Name</th>
                  <th>Action</th>
                  <th className="biz-col-actions">Delete</th>
                </tr>
              </thead>
              <tbody>
                {savedAttributes.map((name, idx) => (
                  <tr key={`${name}-${idx}`}>
                    <td className="biz-col-index">{idx + 1}</td>
                    <td>{name}</td>
                    <td>{savedActions[idx] || "No Action"}</td>
                    <td className="biz-col-actions">
                      <div className="biz-table-actions">
                        <button
                          type="button"
                          className="biz-btn-danger biz-btn-sm"
                          onClick={() => removeSaved(idx)}
                          disabled={saving}
                          title="Delete attribute"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modalOpen && (
        <div className="biz-modal-backdrop" onClick={() => setModalOpen(false)}>
          <div
            className="biz-modal-card biz-attr-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="add-attr-title"
          >
            <button
              type="button"
              className="biz-modal-close"
              onClick={() => setModalOpen(false)}
              aria-label="Close"
            >
              ×
            </button>
            <h3 id="add-attr-title">Add User Attributes</h3>
            <form onSubmit={onSubmit}>
              {rows.map((row, idx) => (
                <div key={idx} className="biz-attr-row">
                  <label className="biz-form-field">
                    <span>
                      Name <span className="biz-required">*</span>
                    </span>
                    <input
                      type="text"
                      name="name"
                      value={row.name}
                      onChange={(e) => updateRow(idx, e)}
                      placeholder="Enter name"
                      required
                      disabled={saving}
                    />
                  </label>
                  <label className="biz-form-field">
                    <span>Action (optional)</span>
                    <input
                      type="text"
                      name="action"
                      value={row.action}
                      onChange={(e) => updateRow(idx, e)}
                      placeholder="Enter action"
                      disabled={saving}
                    />
                  </label>
                </div>
              ))}
              {duplicateError && (
                <p className="biz-form-error">{duplicateError}</p>
              )}
              <div className="biz-modal-actions">
                <button
                  type="button"
                  className="biz-btn-secondary"
                  onClick={addRow}
                  disabled={saving}
                >
                  Add another
                </button>
                <button
                  type="submit"
                  className="biz-btn-primary"
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Attribute;
